import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Archive, RotateCcw, ShieldAlert, Trash2, Undo2 } from "lucide-react";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { deliveryErrorKey, platformErrorMessage } from "../../lib/platformValidation";
import { purgeErrorKey } from "../../lib/purgeConfirm";
import { fmtDateTime } from "../../lib/format";
import ConfirmDialog from "../ConfirmDialog";
import PlatformDeletionRequestDialog from "./PlatformDeletionRequestDialog";
import PlatformPurgeDialog from "./PlatformPurgeDialog";

// The three reversible actions. Each is one POST with an empty body: the
// organization comes from the path and the acting administrator from the verified
// JWT, never from a request body.
const PLAIN_ACTIONS = {
  archive: {
    path: "archive",
    title: "platform.archive_title",
    confirm: "platform.archive_confirm",
    busy: "platform.archiving",
    notice: "platform.archived_notice",
    tone: "danger",
    testId: "platform-archive-dialog",
  },
  restore: {
    path: "restore",
    title: "platform.restore_title",
    confirm: "platform.restore_confirm",
    busy: "platform.restoring",
    notice: "platform.restored_notice",
    tone: "primary",
    testId: "platform-restore-dialog",
  },
  cancel: {
    path: "deletion-cancel",
    title: "platform.deletion_cancel_title",
    confirm: "platform.deletion_cancel_confirm",
    busy: "platform.deletion_cancelling",
    notice: "platform.deletion_cancelled_notice",
    tone: "primary",
    testId: "platform-deletion-cancel-dialog",
  },
};

const JobRow = ({ label, value, testId }) => (
  <div className="qw-life-row">
    <span className="qw-life-label">{label}</span>
    <span data-testid={testId}>{value}</span>
  </div>
);

/**
 * The destructive lifecycle of one organization (Phase 2D.1C checkpoint 3).
 *
 * Every button is rendered from a backend boolean (`can_archive`, `can_restore`,
 * `can_request_deletion`, `can_cancel_deletion`, `can_purge`), so the console can
 * never offer a transition the database would refuse. Archiving, restoring and
 * cancelling destroy nothing and need one confirmation. Requesting deletion and
 * purging require the exact organization name to be retyped, and purging also an
 * explicit acknowledgement — and both are re-validated server-side.
 *
 * A failed purge is a visible, diagnosable, retryable state: the ledger row keeps
 * aggregate counts and a sanitized code, the organization and every tenant row are
 * still there, and the same action can simply be run again.
 */
export const PlatformLifecycleActions = ({ snapshot, organizationId, onSnapshot, onPurged }) => {
  const { t } = useTranslation();
  const [plain, setPlain] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const inFlight = useRef(false);

  const lifecycle = snapshot?.lifecycle || {};
  const organization = snapshot?.organization || {};
  const job = snapshot?.purge || null;

  const reset = () => {
    setError("");
    setNotice("");
  };

  const run = async (key) => {
    const action = PLAIN_ACTIONS[key];
    if (inFlight.current || busy) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await api.post(
        `/platform-admin/organizations/${organizationId}/${action.path}`,
        {},
        { skipOrgHeader: true },
      );
      onSnapshot(r.data);
      setPlain(null);
      const delivery = r.data?.delivery;
      if (delivery && delivery.delivered === false) {
        setError(`${t("platform.delivery_not_sent")} ${t(deliveryErrorKey(delivery.error_code))}`);
        return;
      }
      setNotice(delivery ? t("platform.restored_invitation_notice") : t(action.notice));
    } catch (err) {
      reportError("platform.lifecycleAction", err);
      setError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const restoresToPending = lifecycle.restores_to === "pending_activation";
  const waitingForDeadline = lifecycle.status === "deletion_pending" && !lifecycle.can_purge;
  const available =
    lifecycle.can_archive ||
    lifecycle.can_restore ||
    lifecycle.can_request_deletion ||
    lifecycle.can_cancel_deletion ||
    lifecycle.can_purge;

  return (
    <div className="qw-card" data-testid="platform-lifecycle-actions">
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{t("platform.danger_section")}</div>
      <p className="qw-help" style={{ marginTop: 0 }} data-testid="platform-danger-lead">
        {t("platform.danger_lead")}
      </p>

      <div className="hstack" style={{ flexWrap: "wrap", marginTop: 8 }}>
        {lifecycle.can_archive && (
          <button
            type="button"
            className="qw-btn danger sm"
            onClick={() => {
              reset();
              setPlain("archive");
            }}
            disabled={busy}
            data-testid="platform-archive-open"
          >
            <Archive size={14} strokeWidth={2} aria-hidden="true" />
            {t("platform.archive_action")}
          </button>
        )}
        {lifecycle.can_restore && (
          <button
            type="button"
            className="qw-btn primary sm"
            onClick={() => {
              reset();
              setPlain("restore");
            }}
            disabled={busy}
            data-testid="platform-restore-open"
          >
            <RotateCcw size={14} strokeWidth={2} aria-hidden="true" />
            {t("platform.restore_action")}
          </button>
        )}
        {lifecycle.can_request_deletion && (
          <button
            type="button"
            className="qw-btn danger sm"
            onClick={() => {
              reset();
              setDialog("deletion");
            }}
            disabled={busy}
            data-testid="platform-deletion-request-open"
          >
            <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
            {t("platform.deletion_request_action")}
          </button>
        )}
        {lifecycle.can_cancel_deletion && (
          <button
            type="button"
            className="qw-btn primary sm"
            onClick={() => {
              reset();
              setPlain("cancel");
            }}
            disabled={busy}
            data-testid="platform-deletion-cancel-open"
          >
            <Undo2 size={14} strokeWidth={2} aria-hidden="true" />
            {t("platform.deletion_cancel_action")}
          </button>
        )}
        {lifecycle.can_purge && (
          <button
            type="button"
            className="qw-btn danger sm"
            onClick={() => {
              reset();
              setDialog("purge");
            }}
            disabled={busy}
            data-testid="platform-purge-open"
          >
            <ShieldAlert size={14} strokeWidth={2} aria-hidden="true" />
            {t("platform.purge_action")}
          </button>
        )}
      </div>

      {!available && (
        <div className="qw-empty" style={{ marginTop: 10 }} data-testid="platform-lifecycle-actions-none">
          {t("platform.no_actions")}
        </div>
      )}

      {waitingForDeadline && (
        <div className="qw-help" style={{ marginTop: 10 }} data-testid="platform-purge-waiting">
          {t("platform.purge_deadline", { deadline: fmtDateTime(lifecycle.delete_after) })}
        </div>
      )}

      {error && (
        <div className="qw-error" style={{ marginTop: 10 }} data-testid="platform-lifecycle-action-error">
          {error}
        </div>
      )}
      {notice && (
        <div
          className="qw-help"
          style={{ color: "#059669", marginTop: 10 }}
          data-testid="platform-lifecycle-action-notice"
        >
          {notice}
        </div>
      )}

      {job && (
        <div style={{ marginTop: 14 }} data-testid="platform-purge-job">
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t("platform.purge_job_section")}</div>
          <div className="qw-life-grid">
            <JobRow
              label={t("platform.purge_job_status")}
              value={t(`platform.purge_job_status_${job.status}`, { defaultValue: "—" })}
              testId="platform-purge-job-status"
            />
            <JobRow
              label={t("platform.purge_job_attempts")}
              value={String(job.attempt_count ?? 0)}
              testId="platform-purge-job-attempts"
            />
            <JobRow
              label={t("platform.purge_job_files")}
              value={`${job.storage_objects_deleted ?? 0} / ${job.storage_objects_total ?? 0}`}
              testId="platform-purge-job-files"
            />
            <JobRow
              label={t("platform.purge_job_updated")}
              value={fmtDateTime(job.updated_at)}
              testId="platform-purge-job-updated"
            />
          </div>
          {job.last_error_code && (
            <div className="qw-error" style={{ marginTop: 8 }} data-testid="platform-purge-job-error">
              {t(purgeErrorKey(job.last_error_code))} {t("platform.purge_retry_hint")}
            </div>
          )}
        </div>
      )}

      {plain && (
        <ConfirmDialog
          open
          title={t(PLAIN_ACTIONS[plain].title)}
          body={
            plain === "restore"
              ? t(restoresToPending ? "platform.restore_body_pending" : "platform.restore_body_active")
              : t(plain === "archive" ? "platform.archive_body" : "platform.deletion_cancel_body")
          }
          confirmLabel={t(PLAIN_ACTIONS[plain].confirm)}
          cancelLabel={t("platform.cancel")}
          submitting={busy}
          submittingLabel={t(PLAIN_ACTIONS[plain].busy)}
          error={error}
          tone={PLAIN_ACTIONS[plain].tone}
          testId={PLAIN_ACTIONS[plain].testId}
          onCancel={() => {
            setPlain(null);
            setError("");
          }}
          onConfirm={() => run(plain)}
        />
      )}

      {dialog === "deletion" && (
        <PlatformDeletionRequestDialog
          organizationId={organizationId}
          organization={organization}
          onCancel={() => setDialog(null)}
          onRequested={(data) => {
            setDialog(null);
            onSnapshot(data);
            setError("");
            setNotice(
              t("platform.deletion_requested_notice", {
                deadline: fmtDateTime(data?.lifecycle?.delete_after),
              }),
            );
          }}
        />
      )}

      {dialog === "purge" && (
        <PlatformPurgeDialog
          organizationId={organizationId}
          organization={organization}
          onCancel={() => setDialog(null)}
          onPurged={(data) => {
            setDialog(null);
            onPurged(data);
          }}
        />
      )}
    </div>
  );
};

export default PlatformLifecycleActions;
